import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Calendar,
  Video,
  Clock,
  Users,
  Play,
  CalendarDays,
  ArrowRight,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const WebinarsPage = () => {
  const upcomingWebinars = [
    {
      title: 'Mastering AI Content Creation for Social Media',
      date: 'March 15, 2026',
      time: '2:00 PM EST',
      host: 'Sarah Johnson',
      role: 'Head of Content',
      attendees: 234,
      image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=300&fit=crop',
      topics: ['AI Tools', 'Content Strategy', 'Automation']
    },
    {
      title: 'Building a Winning Social Media Strategy in 2026',
      date: 'March 22, 2026',
      time: '1:00 PM EST',
      host: 'Mike Chen',
      role: 'VP of Marketing',
      attendees: 189,
      image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600&h=300&fit=crop',
      topics: ['Strategy', 'Planning', 'ROI']
    },
    {
      title: 'Advanced Analytics: Measuring What Matters',
      date: 'March 29, 2026',
      time: '3:00 PM EST',
      host: 'Emma Wilson',
      role: 'Data Analyst',
      attendees: 156,
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&h=300&fit=crop',
      topics: ['Analytics', 'Reporting', 'KPIs']
    }
  ];

  const pastWebinars = [
    {
      title: 'Instagram Reels Masterclass',
      date: 'Feb 28, 2026',
      views: '2.5K',
      duration: '45 min',
      image: 'https://images.unsplash.com/photo-1611162618071-b39a2ec055fb?w=400&h=200&fit=crop'
    },
    {
      title: 'LinkedIn for B2B: Best Practices',
      date: 'Feb 21, 2026',
      views: '1.8K',
      duration: '52 min',
      image: 'https://images.unsplash.com/photo-1611944212129-29977ae1398c?w=400&h=200&fit=crop'
    },
    {
      title: 'TikTok Algorithm Decoded',
      date: 'Feb 14, 2026',
      views: '3.2K',
      duration: '38 min',
      image: 'https://images.unsplash.com/photo-1611605698335-8b1569810432?w=400&h=200&fit=crop'
    },
    {
      title: 'Content Calendar Planning 101',
      date: 'Feb 7, 2026',
      views: '1.4K',
      duration: '41 min',
      image: 'https://images.unsplash.com/photo-1506784365847-bbad939e9335?w=400&h=200&fit=crop'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="border-b border-slate-100 bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/stellisocial-icon.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
              <span className="font-bold text-xl text-slate-900" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-500">Social</span></span>
            </Link>
            <div className="hidden md:flex items-center gap-8">
              <Link to="/features" className="text-slate-600 hover:text-slate-900">Features</Link>
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900">Pricing</Link>
              <Link to="/blog" className="text-slate-600 hover:text-slate-900">Blog</Link>
              <Link to="/webinars" className="text-emerald-600 font-medium">Webinars</Link>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="bg-emerald-500 hover:bg-emerald-600">Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-b from-blue-500 to-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-white/20 text-white mb-4">Webinars</Badge>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Learn from the Experts
          </h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto mb-8">
            Join our live webinars and on-demand sessions to master social media marketing.
          </p>
          <div className="flex items-center justify-center gap-4 text-white/80">
            <span className="flex items-center gap-2">
              <Video className="w-5 h-5" /> Live Sessions
            </span>
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5" /> Expert Hosts
            </span>
            <span className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> Free to Attend
            </span>
          </div>
        </div>
      </section>

      {/* Upcoming Webinars */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Upcoming Webinars</h2>
              <p className="text-slate-600">Reserve your spot for these upcoming live sessions</p>
            </div>
            <Badge variant="secondary" className="hidden sm:flex">
              <CalendarDays className="w-4 h-4 mr-1" /> March 2026
            </Badge>
          </div>
          <div className="space-y-6">
            {upcomingWebinars.map((webinar, i) => (
              <motion.div
                key={webinar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="overflow-hidden border-slate-100 hover:border-blue-200 hover:shadow-lg transition-all">
                  <div className="grid md:grid-cols-3">
                    <div className="h-48 md:h-auto bg-slate-100">
                      <img 
                        src={webinar.image} 
                        alt={webinar.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <CardContent className="p-6 md:col-span-2 flex flex-col justify-center">
                      <div className="flex flex-wrap gap-2 mb-3">
                        {webinar.topics.map(topic => (
                          <Badge key={topic} variant="secondary">{topic}</Badge>
                        ))}
                      </div>
                      <h3 className="text-xl font-semibold text-slate-900 mb-2">{webinar.title}</h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 mb-4">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-4 h-4" /> {webinar.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" /> {webinar.time}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-4 h-4" /> {webinar.attendees} registered
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-slate-900 font-medium">{webinar.host}</p>
                          <p className="text-sm text-slate-500">{webinar.role}</p>
                        </div>
                        <Button className="bg-blue-500 hover:bg-blue-600">
                          Register Free <ArrowRight className="ml-2 w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Webinars */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-2xl font-bold text-slate-900">On-Demand Library</h2>
              <p className="text-slate-600">Watch our past webinars anytime</p>
            </div>
            <Button variant="outline">View All Recordings</Button>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pastWebinars.map((webinar, i) => (
              <motion.div
                key={webinar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <Card className="overflow-hidden border-slate-200 hover:shadow-lg transition-shadow cursor-pointer">
                  <div className="relative h-32 bg-slate-100">
                    <img 
                      src={webinar.image} 
                      alt={webinar.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-blue-600 ml-1" />
                      </div>
                    </div>
                    <Badge className="absolute bottom-2 right-2 bg-black/70">{webinar.duration}</Badge>
                  </div>
                  <CardContent className="p-4">
                    <h3 className="font-medium text-slate-900 mb-2 line-clamp-2">{webinar.title}</h3>
                    <div className="flex items-center justify-between text-sm text-slate-500">
                      <span>{webinar.date}</span>
                      <span>{webinar.views} views</span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Never Miss a Webinar</h2>
          <p className="text-blue-100 mb-8">Subscribe to get notified about upcoming live sessions and new recordings.</p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50">
              Get Notified
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <img src="/stellisocial-icon-square-transparent.svg" alt="StelliSocial" className="w-8 h-8 rounded-lg" />
                <span className="font-bold  text-white" style={{fontFamily: "'Tenor Sans', Georgia, serif"}}>Stelli<span className="text-emerald-400">Social</span></span>
              </div>
              <p className="text-sm">The complete social media management platform for teams.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Product</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/features" className="hover:text-white">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
                <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
                <li><Link to="/developers" className="hover:text-white">API</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/blog" className="hover:text-white">Blog</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
                <li><Link to="/community" className="hover:text-white">Community</Link></li>
                <li><Link to="/webinars" className="hover:text-white">Webinars</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/about" className="hover:text-white">About</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/privacy" className="hover:text-white">Privacy</Link></li>
                <li><Link to="/terms" className="hover:text-white">Terms</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 mt-8 pt-8 text-sm text-center">
            &copy; {new Date().getFullYear()} StelliSocial. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default WebinarsPage;
